import React, { useState, useEffect } from 'react';
import { RiskState, Mitigation } from '../../types/bcp';
import ConsolidatedButton from './ConsolidatedButton';

interface MitigationsStepProps {
  risks: RiskState;
  formData: any;
  setFormData: (data: any) => void;
  handleFieldChange: (field: string, value: any) => void;
  handleFieldBlur: (field: string) => void;
  shouldShowError: (field: string) => boolean;
  getFieldError: (field: string) => string | undefined;
  onGenerateAIMitigations?: () => void;
  isLoadingAIMitigations?: boolean;
}

const MitigationsStep: React.FC<MitigationsStepProps> = ({
  risks,
  formData,
  setFormData,
  handleFieldChange,
  handleFieldBlur,
  shouldShowError,
  getFieldError,
  onGenerateAIMitigations,
  isLoadingAIMitigations = false,
}) => {
  const [mitigations, setMitigations] = useState<Mitigation[]>([]);
  const [showMitigationModal, setShowMitigationModal] = useState(false);
  const [editingMitigation, setEditingMitigation] = useState<Mitigation | null>(null);

  // Generate unique ID
  const generateId = () => Math.random().toString(36).substr(2, 9);

  // Initialize mitigations from form data
  useEffect(() => {
    if (formData.mitigations) {
      setMitigations(formData.mitigations);
    }
  }, [formData.mitigations]);

  // Add mitigation
  const addMitigation = (mitigationData: Omit<Mitigation, 'id'>) => {
    // Check for duplicates
    const normalizeString = (str: string) =>
      str.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();

    const isDuplicate = mitigations.some((existing) => {
      const sameRisk = normalizeString(existing.risk_name) === normalizeString(mitigationData.risk_name);
      const sameStrategy = normalizeString(existing.mitigation_strategy) === normalizeString(mitigationData.mitigation_strategy);
      return sameRisk && sameStrategy;
    });

    if (isDuplicate) {
      alert('A similar mitigation strategy for this risk already exists. Please modify your strategy or update the existing one.');
      return;
    }

    const newMitigation: Mitigation = {
      ...mitigationData,
      id: generateId(),
    };
    const updatedMitigations = [...mitigations, newMitigation];
    setMitigations(updatedMitigations);
    handleFieldChange('mitigations', updatedMitigations);
    setShowMitigationModal(false);
    setEditingMitigation(null);
  };

  // Update mitigation
  const updateMitigation = (mitigationData: Mitigation) => {
    const updatedMitigations = mitigations.map(mitigation =>
      mitigation.id === mitigationData.id ? mitigationData : mitigation
    );
    setMitigations(updatedMitigations);
    handleFieldChange('mitigations', updatedMitigations);
    setShowMitigationModal(false);
    setEditingMitigation(null);
  };

  // Handle mitigation save with proper typing
  const handleMitigationSave = (mitigation: Mitigation | Omit<Mitigation, 'id'>) => {
    if ('id' in mitigation) {
      // This is an update operation
      updateMitigation(mitigation as Mitigation);
    } else {
      // This is an add operation
      addMitigation(mitigation as Omit<Mitigation, 'id'>);
    }
  };

  // Remove mitigation
  const removeMitigation = (id: string) => {
    const updatedMitigations = mitigations.filter(mitigation => mitigation.id !== id);
    setMitigations(updatedMitigations);
    handleFieldChange('mitigations', updatedMitigations);
  };

  // Get effectiveness color
  const getEffectivenessColor = (effectiveness: string) => {
    switch (effectiveness) {
      case 'high':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'planned':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'not_applicable':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Risk Mitigations</h2>
          <p className="text-gray-600">Define strategies to mitigate identified risks</p>
        </div>
        <div className="flex space-x-2">
          <ConsolidatedButton
            label="Add New"
            variant="blue"
            options={[
              {
                label: 'Fill Form',
                value: 'fill_form',
                icon: '➕',
                onClick: () => {
                  setEditingMitigation(null);
                  setShowMitigationModal(true);
                },
              },
              {
                label: 'From AI',
                value: 'from_ai',
                icon: '🤖',
                onClick: () => {
                  if (onGenerateAIMitigations) {
                    onGenerateAIMitigations();
                  }
                },
                disabled: isLoadingAIMitigations,
                loading: isLoadingAIMitigations
              },
            ]}
          />
        </div>
      </div>

      {/* Risk Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">Identified Risks</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {risks.primary_concerns.map((risk, index) => (
            <div key={index} className="bg-white rounded-lg p-3 border border-blue-100">
              <div className="flex flex-col gap-2">
                <span className="font-medium text-gray-900">{risk}</span>
                <span className="text-sm text-blue-600">
                  {mitigations.filter(m => m.risk_name === risk).length} mitigation(s)
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mitigations List */}
      <div className="space-y-4">
        {mitigations.length > 0 ? (
          mitigations.map((mitigation) => (
            <div key={mitigation.id} className="p-4 bg-white rounded-lg border border-gray-200">
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="font-semibold text-gray-900">{mitigation.risk_name}</h4>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getEffectivenessColor(mitigation.effectiveness)}`}>
                      {mitigation.effectiveness.toUpperCase()} EFFECTIVENESS
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(mitigation.status)}`}>
                      {mitigation.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{mitigation.mitigation_strategy}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setEditingMitigation(mitigation);
                      setShowMitigationModal(true);
                    }}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => removeMitigation(mitigation.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
                <div>
                  <span className="font-medium text-gray-700">Responsible:</span>
                  <p className="text-gray-600">{mitigation.responsible_party}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Timeline:</span>
                  <p className="text-gray-600">{mitigation.timeline}</p>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="p-8 text-center rounded-lg border-2 border-gray-300 border-dashed">
            <svg className="mx-auto w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No mitigations defined</h3>
            <p className="mt-1 text-sm text-gray-500">Add mitigation strategies for your identified risks.</p>
          </div>
        )}
      </div>

      {/* Mitigation Modal */}
      {showMitigationModal && (
        <MitigationModal
          mitigation={editingMitigation}
          risks={risks.primary_concerns}
          onSave={handleMitigationSave}
          onClose={() => {
            setShowMitigationModal(false);
            setEditingMitigation(null);
          }}
        />
      )}
    </div>
  );
};

// Mitigation Modal Component
interface MitigationModalProps {
  mitigation: Mitigation | null;
  risks: string[];
  onSave: (mitigation: Mitigation | Omit<Mitigation, 'id'>) => void;
  onClose: () => void;
}

const MitigationModal: React.FC<MitigationModalProps> = ({ mitigation, risks, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    risk_name: mitigation?.risk_name || '',
    mitigation_strategy: mitigation?.mitigation_strategy || '',
    detailed_strategy: mitigation?.detailed_strategy || '',
    incident_response_steps: mitigation?.incident_response_steps || [],
    responsible_party: mitigation?.responsible_party || '',
    timeline: mitigation?.timeline || '',
    effectiveness: mitigation?.effectiveness || 'medium' as 'high' | 'medium' | 'low',
    status: mitigation?.status || 'planned' as 'planned' | 'in_progress' | 'completed' | 'not_applicable',
    specific_actions: mitigation?.specific_actions || [],
    resources_required: mitigation?.resources_required || '',
    implementation_priority: mitigation?.implementation_priority || '',
    maintenance_requirements: mitigation?.maintenance_requirements || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mitigation) {
      onSave({ ...formData, id: mitigation.id } as Mitigation);
    } else {
      onSave(formData as Omit<Mitigation, 'id'>);
    }
  };

  return (
    <div className="overflow-y-auto fixed inset-0 z-50">
      <div className="flex justify-center items-center px-4 pt-4 pb-20 min-h-screen text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="inline-block overflow-hidden p-6 my-8 w-full max-w-2xl text-left align-middle bg-white rounded-2xl shadow-xl transition-all transform">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {mitigation ? 'Edit Mitigation' : 'Add Mitigation'}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Risk <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.risk_name}
                onChange={(e) => setFormData(prev => ({ ...prev, risk_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select a risk</option>
                {risks.map((risk, index) => (
                  <option key={index} value={risk}>{risk}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mitigation Strategy <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.mitigation_strategy}
                onChange={(e) => setFormData(prev => ({ ...prev, mitigation_strategy: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Brief mitigation strategy name..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Detailed Strategy <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.detailed_strategy}
                onChange={(e) => setFormData(prev => ({ ...prev, detailed_strategy: e.target.value }))}
                rows={8}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Provide detailed strategy description (up to 15 lines) covering prevention, detection, response, and recovery..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Incident Response Steps <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {formData.incident_response_steps.map((step, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500 w-8">{index + 1}.</span>
                    <input
                      type="text"
                      value={step}
                      onChange={(e) => {
                        const newSteps = [...formData.incident_response_steps];
                        newSteps[index] = e.target.value;
                        setFormData(prev => ({ ...prev, incident_response_steps: newSteps }));
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={`Step ${index + 1}: Describe the action...`}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newSteps = formData.incident_response_steps.filter((_, i) => i !== index);
                        setFormData(prev => ({ ...prev, incident_response_steps: newSteps }));
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    incident_response_steps: [...prev.incident_response_steps, '']
                  }))}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  + Add Response Step
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Responsible Party <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.responsible_party}
                  onChange={(e) => setFormData(prev => ({ ...prev, responsible_party: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., IT Department, Operations Manager"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Timeline <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.timeline}
                  onChange={(e) => setFormData(prev => ({ ...prev, timeline: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 30 days, Q1 2024"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Effectiveness <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.effectiveness}
                  onChange={(e) => setFormData(prev => ({ ...prev, effectiveness: e.target.value as 'high' | 'medium' | 'low' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as 'planned' | 'in_progress' | 'completed' | 'not_applicable' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="planned">Planned</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="not_applicable">Not Applicable</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Specific Actions
              </label>
              <div className="space-y-2">
                {formData.specific_actions.map((action, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500 w-8">{index + 1}.</span>
                    <input
                      type="text"
                      value={action}
                      onChange={(e) => {
                        const newActions = [...formData.specific_actions];
                        newActions[index] = e.target.value;
                        setFormData(prev => ({ ...prev, specific_actions: newActions }));
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={`Action ${index + 1}: Describe the specific action...`}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newActions = formData.specific_actions.filter((_, i) => i !== index);
                        setFormData(prev => ({ ...prev, specific_actions: newActions }));
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    specific_actions: [...prev.specific_actions, '']
                  }))}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  + Add Action
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Resources Required
                </label>
                <input
                  type="text"
                  value={formData.resources_required}
                  onChange={(e) => setFormData(prev => ({ ...prev, resources_required: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Personnel, technology, equipment needed..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Implementation Priority
                </label>
                <select
                  value={formData.implementation_priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, implementation_priority: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select priority</option>
                  <option value="Immediate">Immediate</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maintenance Requirements
              </label>
              <textarea
                value={formData.maintenance_requirements}
                onChange={(e) => setFormData(prev => ({ ...prev, maintenance_requirements: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Describe ongoing maintenance and review requirements..."
              />
            </div>

            <div className="flex justify-end pt-4 space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                {mitigation ? 'Update Mitigation' : 'Add Mitigation'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MitigationsStep;
